import { z } from 'zod';

// Base object schema with all fields
export const objectFormSchema = z.object({
    // Object Details Tab
    ObjectService: z.enum(['Object', 'Service']).default('Object'),
    
    ObjectID: z.string()
        .min(1, 'Object ID is required')
        .max(250, 'Object ID must be less than 250 characters')
        .regex(/^[A-Za-z0-9-]+$/, {
            message: 'Object ID must only contain letters, numbers and hyphens',
        }),
    
    Description: z.string()
        .max(1000, 'Description must be less than 1000 characters')
        .nullable()
        .optional(),
    
    LayerID: z.union([
        z.number().int().positive({
            message: 'Layer ID is required'
        }),
        z.null(),
        z.undefined()
    ]).refine((val) => val !== null && val !== undefined, {
        message: 'Layer ID is required'
    }),
    
    ResponsibleTeam: z.number().int().positive().nullable().optional(),
    
    RPO: z.union([
        z.string(), // Dropdown value
        z.number().int().min(0) // Minutes entered directly
    ]).nullable().optional(),
    
    RTO: z.union([
        z.string(), // Dropdown value
        z.number().int().min(0) // Minutes entered directly
    ]).nullable().optional(),
    
    Location: z.number().int().positive().nullable().optional(),
    
    ManagementOnly: z.number().int().min(0).max(1).default(0),
    
    BackupSolution: z.union([
        z.number().int().positive(),
        z.literal(0),
        z.null()
    ]).optional(),
    
    Datastore: z.number().int().min(0).max(1).default(0),
    
    ApplicationEncryption: z.number().int().min(0).max(1).default(0),
    
    Analytics: z.enum(['Yes', 'No']).default('No'),
    
    Icon: z.string().nullable().optional(), // Placeholder for image server
    
    Technology: z.number().int().positive().nullable().optional(),
    
    // Floor Plan Tab (placeholder)
    FloorPlanImage: z.string().nullable().optional(),
    FloorPlanMarker: z.any().nullable().optional(),
    
    // Recovery Information Tab
    Instructions: z.string().nullable().optional(), // HTML content
    
    // Recovery Document/Video Tab
    RecoveryDocument: z.number().int().positive().nullable().optional(),
    RecoveryVideo: z.number().int().positive().nullable().optional(),
    
    // Runbook Tab
    MasterRunbook: z.number().int().positive().nullable().optional(),
    RunbookTasks: z.array(z.object({
        id: z.number().int().nullable().optional(),
        taskNo: z.number().int().nullable(),
        task: z.string(),
        commands: z.string(),
        duration: z.number().int().nullable(),
        taskOffset: z.number().int().nullable().optional(), // Minutes from runbook start
        startTime: z.string().nullable().optional(), // Deprecated: calculated from taskOffset for display
        finishTime: z.string().nullable().optional(), // Deprecated: calculated from taskOffset for display
        dependencies: z.string(),
    })).optional(),
    
    // Object Picture Tab
    ObjectImage: z.string().nullable().optional(), // Placeholder for image server
});

// Validation function for BackupSolution
const backupSolutionValidation = (data: any) => {
    // If ManagementOnly is 0, BackupSolution must be 0 or null
    if (data.ManagementOnly === 0 && data.BackupSolution !== null && data.BackupSolution !== 0) {
        return false;
    }
    return true;
};

export const createObjectSchema = objectFormSchema
    .refine(backupSolutionValidation, {
        message: 'Backup Solution can only be set when Management Only is enabled',
        path: ['BackupSolution'],
    })
    .refine((data) => {
        // LayerID is required when ObjectService is 'Object'
        if (data.ObjectService === 'Object' && (data.LayerID === null || data.LayerID === undefined)) {
            return false;
        }
        return true;
    }, {
        message: 'Layer ID is required',
        path: ['LayerID'],
    })
    .refine((data) => {
        // When ObjectService is 'Service', certain fields must be null
        if (data.ObjectService === 'Service') {
            // These fields should be null for services
            return true; // Validation passed, we'll set them to null in handleSubmit
        }
        return true;
    });

export const updateObjectSchema = objectFormSchema
    .partial()
    .extend({
        ObjectID: z.string().optional(), // ObjectID can't be changed after creation
    })
    .refine((data) => {
        // Only validate if ManagementOnly and BackupSolution are provided
        if (data.ManagementOnly !== undefined && data.BackupSolution !== undefined) {
            return backupSolutionValidation(data);
        }
        return true;
    }, {
        message: 'Backup Solution can only be set when Management Only is enabled',
        path: ['BackupSolution'],
    });

export type ObjectFormData = z.infer<typeof objectFormSchema>;
export type CreateObjectFormData = z.infer<typeof createObjectSchema>;
export type UpdateObjectFormData = z.infer<typeof updateObjectSchema>;
